import { Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { ReactElement } from 'react'
import { Button } from '@/components/ui/button'
import { CreateTrackedItemDialog } from '@/features/tracker/CreateTrackedItemDialog'
import { useCreateRequestTrackedItem, useDismissRequest, useLinkRequest, useRestoreRequest, useUnlinkRequest } from '@/queries/clientMeetings'
import type { CallRequest, RequestSource } from '@/types/clientMeetings'
import { ChangeLinkDialog } from './ChangeLinkDialog'
import { CreateRequestLinearDialog } from './CreateRequestLinearDialog'

export interface RequestContext {
  source: RequestSource
  companyId: string
  companyName: string
  trackerEnabled: boolean
}

const GRID = 'grid w-full grid-cols-2 gap-1.5'

function ActionButton({
  label,
  onClick,
  disabled,
  variant = 'outline',
}: {
  label: string
  onClick?: () => void
  disabled?: boolean
  variant?: 'default' | 'outline'
}) {
  return (
    <Button size="xs" variant={variant} disabled={disabled} onClick={onClick} className="w-full justify-center">
      {label}
    </Button>
  )
}

/** A bin icon that dismisses an unlinked request, shown at the end of its row. */
export function DismissRequestButton({ request }: { request: CallRequest }) {
  const dismiss = useDismissRequest()
  if (request.link || request.dismissed) return null
  return (
    <Button
      size="icon-sm"
      variant="ghost"
      aria-label="Dismiss"
      title="Dismiss"
      disabled={dismiss.isPending}
      onClick={() => dismiss.mutate({ requestId: request._id })}
      className="text-etn-text-soft hover:bg-etn-danger-subtle hover:text-etn-danger-text"
    >
      <Trash2 className="size-[18px]" />
    </Button>
  )
}

/** "Add to GER-38": link a request to the ticket the AI thought closest, shown beside that hint. */
export function AddToHintButton({ request }: { request: CallRequest }) {
  const linkRequest = useLinkRequest()
  const hint = request.hint
  if (!hint || request.link || request.dismissed) return null
  return (
    <Button size="xs" disabled={linkRequest.isPending} onClick={() => linkRequest.mutate({ requestId: request._id, kind: hint.kind, id: hint.id })}>
      Add to {hint.ref}
    </Button>
  )
}

export function RequestActions({ request, context }: { request: CallRequest; context: RequestContext }) {
  const [changing, setChanging] = useState(false)
  const [raisingLinear, setRaisingLinear] = useState(false)
  const unlink = useUnlinkRequest()
  const restore = useRestoreRequest()
  const createTracked = useCreateRequestTrackedItem()
  const target = { requestId: request._id }
  const dialogs = (
    <>
      {changing && <ChangeLinkDialog request={request} companyId={context.companyId} open={changing} onOpenChange={setChanging} />}
      {raisingLinear && (
        <CreateRequestLinearDialog request={request} source={context.source} companyName={context.companyName} open={raisingLinear} onOpenChange={setRaisingLinear} />
      )}
    </>
  )

  if (request.dismissed) {
    return (
      <div data-testid="request-actions" className={GRID}>
        <span className="self-center truncate text-[12px] text-etn-text-soft">{request.dismissed.byName ? `Dismissed by ${request.dismissed.byName}` : 'Dismissed'}</span>
        <ActionButton label="Undo" disabled={restore.isPending} onClick={() => restore.mutate(target)} />
      </div>
    )
  }

  if (request.link) {
    return (
      <div data-testid="request-actions" className={GRID}>
        <ActionButton label="Change link" onClick={() => setChanging(true)} />
        <ActionButton label="Unlink" disabled={unlink.isPending} onClick={() => unlink.mutate(target)} />
        {dialogs}
      </div>
    )
  }

  const createTracker = (trigger: ReactElement) => (
    <CreateTrackedItemDialog
      trigger={trigger}
      initial={{
        company: { _id: context.companyId, name: context.companyName },
        requester: request.suggestedRequester,
        subject: request.title,
        description: request.detail,
        category: request.suggestedCategory ?? '',
        notify: false,
      }}
      onCreate={body => createTracked.mutateAsync({ ...target, body: { ...body, type: body.type || undefined } })}
    />
  )
  return (
    <div data-testid="request-actions" className={GRID}>
      {context.trackerEnabled &&
        createTracker(
          <Button size="xs" className="w-full justify-center">
            Create tracker issue
          </Button>
        )}
      <Button size="xs" variant="secondary" onClick={() => setRaisingLinear(true)} className="w-full justify-center">
        <Send />
        Create Linear issue
      </Button>
      <ActionButton label="Link to a ticket" onClick={() => setChanging(true)} />
      {dialogs}
    </div>
  )
}
